import React, { useState, useEffect, useCallback } from 'react';
import DynamicSidebar from '../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../components/ui/DashboardHeader';
import Icon from '../../../components/AppIcon';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import CreateSettlementModal from './components/CreateSettlementModal';

const STATUS_CONFIG = {
  COMPLETED: { label: 'Completed', color: 'bg-success/10 text-success border-success/20', icon: 'BadgeCheck' },
  PENDING: { label: 'Pending', color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock' },
  PROCESSING: { label: 'Processing', color: 'bg-primary/10 text-primary border-primary/20', icon: 'Loader' },
  FAILED: { label: 'Failed', color: 'bg-error/10 text-error border-error/20', icon: 'XCircle' },
};

const getTodayStr = () => {
  const d = new Date();
  return d?.toISOString()?.split('T')?.[0];
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
};

const formatCurrency = (val) => {
  if (val == null || val === '') return '-';
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' })?.format(num);
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || { label: status || '-', color: 'bg-muted text-text-secondary border-border', icon: 'Circle' };
  return (
    <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} />
      <span>{cfg?.label}</span>
    </span>
  );
};

const MerchantDetailRow = ({ merchantId, startDate, endDate }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const res = await apiService?.getSettlementMerchantDetail(params);
        const details = res?.data?.details || [];
        // Find matching merchant_id or use first result
        const found = details?.find(d => d?.merchant_id === merchantId) || details?.[0] || null;
        setDetail(found);
      } catch (e) {
        setError('Failed to load merchant details');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [merchantId, startDate, endDate]);

  if (loading) {
    return (
      <tr className="bg-muted/20">
        <td colSpan={9} className="px-6 py-4">
          <div className="flex items-center space-x-2 text-text-secondary text-sm">
            <Icon name="Loader" size={14} className="animate-spin" />
            <span>Loading merchant details...</span>
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr className="bg-muted/20">
        <td colSpan={9} className="px-6 py-4">
          <p className="text-sm text-error">{error}</p>
        </td>
      </tr>
    );
  }

  if (!detail) {
    return (
      <tr className="bg-muted/20">
        <td colSpan={9} className="px-6 py-4">
          <p className="text-sm text-text-secondary">No detail found for merchant: {merchantId}</p>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-primary/5 border-b border-border">
      <td colSpan={9} className="px-6 py-4">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="User" size={14} className="text-primary" />
          <span className="text-sm font-semibold text-text-primary">Merchant Detail — {detail?.merchant_id}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { label: 'Deposits', value: formatCurrency(detail?.deposits), icon: 'ArrowDownCircle', color: 'text-success' },
            { label: 'Payouts', value: formatCurrency(detail?.payouts), icon: 'ArrowUpCircle', color: 'text-warning' },
            { label: 'Refunds', value: formatCurrency(detail?.refunds), icon: 'RotateCcw', color: 'text-error' },
            { label: 'MDR', value: formatCurrency(detail?.mdr), icon: 'Percent', color: 'text-text-secondary' },
            { label: 'Net', value: formatCurrency(detail?.net), icon: 'TrendingUp', color: 'text-primary' },
          ]?.map(item => (
            <div key={item?.label} className="bg-surface border border-border rounded-lg p-3">
              <div className="flex items-center space-x-1.5 mb-1">
                <Icon name={item?.icon} size={13} className={item?.color} />
                <span className="text-xs text-text-secondary">{item?.label}</span>
              </div>
              <p className="text-sm font-bold text-text-primary">{item?.value}</p>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
};

const SettlementsPage = () => {
  const { businessCode } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const today = getTodayStr();
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState(today);
  const [filterTo, setFilterTo] = useState(today);

  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const fetchSettlements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const bc = businessCode || localStorage.getItem('dp_business_code') || '';
      const params = {
        start_date: filterFrom || today,
        end_date: filterTo || today,
      };
      if (bc) params.business_code = bc;
      if (filterStatus) params.status = filterStatus;

      const res = await apiService?.getSettlementsList(params);
      setSettlements(res?.data?.details || []);
      setPage(1);
    } catch (e) {
      setError(e?.response?.data?.status_description || 'Failed to load settlements');
      setSettlements([]);
    } finally {
      setLoading(false);
    }
  }, [businessCode, filterStatus, filterFrom, filterTo]);

  useEffect(() => {
    fetchSettlements();
  }, [fetchSettlements]);

  const totalPages = Math.max(1, Math.ceil(settlements?.length / PAGE_SIZE));
  const paginated = settlements?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleExpandToggle = (settlementId) => {
    setExpandedRow(prev => (prev === settlementId ? null : settlementId));
  };

  const getMerchantIds = (merchantsField) => {
    if (!merchantsField) return [];
    return merchantsField?.split('-')?.map(s => s?.trim())?.filter(Boolean);
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(prev => !prev)} />
      <div className="flex">
        <DynamicSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} mt-16 p-6`}>

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-secondary mb-1">
              <span>Payment Operations</span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">Settlements</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Settlements</h1>
                <p className="text-text-secondary text-sm mt-1">Manage and track all merchant settlement transactions</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
                >
                  <Icon name="Plus" size={15} />
                  <span>Create Settlement</span>
                </button>
                <button
                  onClick={fetchSettlements}
                  className="flex items-center space-x-2 px-4 py-2 bg-surface border border-border text-text-primary rounded-lg text-sm font-medium hover:bg-muted transition-smooth"
                >
                  <Icon name="RefreshCw" size={15} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-surface border border-border rounded-xl p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={e => { setFilterStatus(e?.target?.value); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">All Statuses</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="FAILED">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Start Date</label>
                <input
                  type="date"
                  value={filterFrom}
                  onChange={e => { setFilterFrom(e?.target?.value); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">End Date</label>
                <input
                  type="date"
                  value={filterTo}
                  onChange={e => { setFilterTo(e?.target?.value); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={fetchSettlements}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
                >
                  <Icon name="Search" size={15} />
                  <span>Apply Filters</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    {['Settlement ID', 'Business', 'Merchants', 'Start Date', 'End Date', 'Net Amount', 'Status', 'Created At', '']?.map(col => (
                      <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center space-y-3">
                          <Icon name="Loader" size={24} className="text-primary animate-spin" />
                          <p className="text-text-secondary text-sm">Loading settlements...</p>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center space-y-3">
                          <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center">
                            <Icon name="AlertCircle" size={24} className="text-error" />
                          </div>
                          <p className="text-text-secondary text-sm">{error}</p>
                          <button onClick={fetchSettlements} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth">
                            Retry
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : paginated?.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center space-y-3">
                          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                            <Icon name="Banknote" size={24} className="text-text-secondary" />
                          </div>
                          <p className="text-text-secondary text-sm">No settlements found for the selected filters</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginated?.map((settlement) => {
                      const sid = settlement?.settlement_Id || settlement?.settlement_id;
                      const isExpanded = expandedRow === sid;
                      const merchantIds = getMerchantIds(settlement?.merchants);

                      return (
                        <React.Fragment key={sid}>
                          <tr className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors group ${isExpanded ? 'bg-muted/20' : ''}`}>
                            <td className="px-4 py-3.5">
                              <span className="text-sm font-mono font-medium text-primary">{sid || '-'}</span>
                            </td>
                            <td className="px-4 py-3.5">
                              <div>
                                <p className="text-sm font-medium text-text-primary">{settlement?.business_name || '-'}</p>
                                <p className="text-xs text-text-secondary font-mono">{settlement?.business_code || ''}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex flex-wrap gap-1">
                                {merchantIds?.length > 0 ? merchantIds?.map(mid => (
                                  <span key={mid} className="text-xs bg-muted text-text-secondary px-1.5 py-0.5 rounded font-mono">{mid}</span>
                                )) : (
                                  <span className="text-sm text-text-secondary">{settlement?.merchants || '-'}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <span className="text-sm text-text-primary whitespace-nowrap">{formatDate(settlement?.start_date)}</span>
                            </td>
                            <td className="px-4 py-3.5">
                              <span className="text-sm text-text-primary whitespace-nowrap">{formatDate(settlement?.end_date)}</span>
                            </td>
                            <td className="px-4 py-3.5">
                              <span className="text-sm font-bold text-text-primary">{formatCurrency(settlement?.netAmount)}</span>
                            </td>
                            <td className="px-4 py-3.5">
                              <StatusBadge status={settlement?.status} />
                            </td>
                            <td className="px-4 py-3.5">
                              <span className="text-sm text-text-secondary whitespace-nowrap">{formatDate(settlement?.created_at)}</span>
                            </td>
                            <td className="px-4 py-3.5">
                              <button
                                onClick={() => handleExpandToggle(sid)}
                                className="p-1 rounded hover:bg-muted transition-colors"
                                title="View merchant details"
                              >
                                <Icon
                                  name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                                  size={16}
                                  className={`transition-colors ${isExpanded ? 'text-primary' : 'text-text-secondary group-hover:text-primary'}`}
                                />
                              </button>
                            </td>
                          </tr>
                          {isExpanded && merchantIds?.map(mid => (
                            <MerchantDetailRow
                              key={mid}
                              merchantId={mid}
                              startDate={settlement?.start_date || filterFrom || today}
                              endDate={settlement?.end_date || filterTo || today}
                            />
                          ))}
                          {isExpanded && merchantIds?.length === 0 && (
                            <MerchantDetailRow
                              key="single"
                              merchantId={settlement?.merchants}
                              startDate={settlement?.start_date || filterFrom || today}
                              endDate={settlement?.end_date || filterTo || today}
                            />
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && !error && settlements?.length > 0 && (
              <div className="px-4 py-3 border-t border-border flex items-center justify-between">
                <p className="text-xs text-text-secondary">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, settlements?.length)} of {settlements?.length} results
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-xs border border-border rounded-lg text-text-secondary hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-text-secondary px-2">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-xs border border-border rounded-lg text-text-secondary hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {showCreateModal && (
        <CreateSettlementModal
          onClose={() => setShowCreateModal(false)}
          onSettlementCreated={() => {
            setShowCreateModal(false);
            fetchSettlements();
          }}
        />
      )}
    </div>
  );
};

export default SettlementsPage;
